import {useCallback, useMemo} from 'react'
import {
  useGetAccessToProductQuery,
  useGetProductPlanQuery,
  useGetProductQuery,
  useGetProductQuotaQuery,
  useGetSubscriptionQuery,
  useGetTrialStatusQuery,
  useRecoverSubscriptionMutation,
} from '../../../../../services/cellcast.v2'
import {
  ProductAccessResponse,
  ProductAccessResponseData,
  ProductPlan,
  Subscription,
} from '../../../../../services/cellcast.v2.types'

export type UseProductSubscriptionResult = {
  productId: string
  productPlans: ProductPlan[]
  productQuota: any | undefined
  productQuotaRaw: any | undefined
  subscription: Subscription | undefined
  trialStatus: any | undefined
  accessData: ProductAccessResponseData | undefined
  currentPlan: ProductPlan | undefined
  usagePercentage: number
  getProgressVariant: () => 'primary' | 'warning' | 'danger'
  reFetchAllData: () => Promise<void>
  recoverSubscription: (args: {subscriptionId: string}) => Promise<any>
  isRecovering: boolean
  isUninitialized: {
    access: boolean
    plans: boolean
    quota: boolean
    subscription: boolean
    trial: boolean
  }
}

export const useProductSubscription = (productKey: string): UseProductSubscriptionResult => {
  // Product base
  const {data: productData} = useGetProductQuery({productKey})
  const productId = productData?.data?.product?._id || ''

  // Queries
  const {
    data: accessToProduct,
    refetch: refetchAccessToProduct,
    isUninitialized: accessUninitialized,
  } = useGetAccessToProductQuery({productId}, {skip: !productId})

  const {
    data: productPlansResponse,
    refetch: refetchProductPlans,
    isUninitialized: plansUninitialized,
  } = useGetProductPlanQuery({productId}, {skip: !productId})

  const {
    data: productQuotaResponse,
    refetch: refetchProductQuota,
    isUninitialized: quotaUninitialized,
  } = useGetProductQuotaQuery({productId}, {skip: !productId})

  const {
    data: subscriptionData,
    refetch: refetchSubscription,
    isUninitialized: subUninitialized,
  } = useGetSubscriptionQuery({productId}, {skip: !productId})

  const {
    data: trialStatusResp,
    isUninitialized: trialStatusUninitialized,
    refetch: refetchTrialStatus,
  } = useGetTrialStatusQuery({productId}, {skip: !productId})

  const [recoverSubscriptionMutation, {isLoading: isRecovering}] = useRecoverSubscriptionMutation()

  const productPlans: ProductPlan[] = productPlansResponse?.data?.plans || []
  const productQuota = productQuotaResponse?.data?.quota_usage
  const productQuotaRaw = productQuotaResponse?.data
  const subscription: Subscription | undefined = subscriptionData?.data?.subscriptions?.[0]

  const currentPlan: ProductPlan | undefined = useMemo(() => {
    if (!subscription) return undefined
    return productPlans.find((plan) => plan._id === subscription?.planId?._id)
  }, [productPlans, subscription])

  const usagePercentage = useMemo(() => {
    if (!productQuota) return 0
    const limit = Number(productQuota.quotaLimit || 0)
    const used = Number(productQuota.currentUsage || 0)
    if (!limit || limit <= 0) return 0
    return Math.min((used / limit) * 100, 100)
  }, [productQuota])

  const getProgressVariant = useCallback((): 'primary' | 'warning' | 'danger' => {
    if (usagePercentage >= 90) return 'danger'
    if (usagePercentage >= 75) return 'warning'
    return 'primary'
  }, [usagePercentage])

  const reFetchAllData = useCallback(async () => {
    if (!productId) return
    const tasks: Array<Promise<any>> = []
    if (!accessUninitialized) tasks.push(refetchAccessToProduct())
    if (!plansUninitialized) tasks.push(refetchProductPlans())
    if (!quotaUninitialized) tasks.push(refetchProductQuota())
    if (!subUninitialized) tasks.push(refetchSubscription())
    if (!trialStatusUninitialized) tasks.push(refetchTrialStatus())
    if (tasks.length) await Promise.all(tasks)
  }, [
    productId,
    accessUninitialized,
    plansUninitialized,
    quotaUninitialized,
    subUninitialized,
    trialStatusUninitialized,
    refetchAccessToProduct,
    refetchProductPlans,
    refetchProductQuota,
    refetchSubscription,
    refetchTrialStatus,
  ])

  const recoverSubscription = useCallback(
    async ({subscriptionId}: {subscriptionId: string}) =>
      await recoverSubscriptionMutation({subscriptionId}),
    [recoverSubscriptionMutation]
  )

  return {
    productId,
    productPlans,
    productQuota,
    productQuotaRaw,
    subscription,
    trialStatus: trialStatusResp?.data,
    accessData: accessToProduct?.data as ProductAccessResponseData | undefined,
    currentPlan,
    usagePercentage,
    getProgressVariant,
    reFetchAllData,
    recoverSubscription,
    isRecovering,
    isUninitialized: {
      access: accessUninitialized,
      plans: plansUninitialized,
      quota: quotaUninitialized,
      subscription: subUninitialized,
      trial: trialStatusUninitialized,
    },
  }
}

export default useProductSubscription
